import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { Input } from "@/lib/components/Input";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useFontsByExternalIdsQuery } from "@/lib/queries/font-queries";
import { FontDto, FontStyleDto } from "@repo/api/font";
import FontDialog from "./FontDialog/FontDialog";
import {
  fontWeightLabels,
  getFontStyleOptions,
  getFontWeightOptions,
} from "./FontDialog/font-utils";
import { cn } from "@/lib/utils";

const styleLabels: Record<string, string> = {
  normal: "Regular",
  italic: "Italic",
};

type FontSelectProps = {
  fontFamily: string;
  fontExternalId?: string;
  fontWeight: number;
  fontStyle: FontStyleDto;
  fontSize: number;
  onSelectFont: (params: {
    font: FontDto;
    fontWeight: number;
    fontStyle: FontStyleDto;
  }) => void;
  onFontWeightChange: (fontWeight: number) => void;
  onFontStyleChange: (fontStyle: FontStyleDto) => void;
  onFontSizeChange: (fontSize: number) => void;
  className?: string;
};

export function FontSelect({
  fontFamily,
  fontExternalId,
  fontWeight,
  fontStyle,
  fontSize,
  onSelectFont,
  onFontWeightChange,
  onFontStyleChange,
  onFontSizeChange,
  className,
}: FontSelectProps) {
  const teamExternalId = useEditorStore((state) => state.teamExternalId);

  const { data: referencedFonts = [] } = useFontsByExternalIdsQuery(
    teamExternalId,
    fontExternalId ? [fontExternalId] : [],
  );
  const selectedFont = referencedFonts.find(
    (font) => font.externalId === fontExternalId,
  );

  const weightOptions = getFontWeightOptions(selectedFont, fontWeight);
  const styleOptions = getFontStyleOptions(selectedFont, fontStyle, fontWeight);

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex items-center gap-4">
        <FontDialog
          selectedFamily={fontFamily}
          selectedExternalId={fontExternalId}
          currentWeight={fontWeight}
          currentStyle={fontStyle}
          onSelectFont={onSelectFont}
        />
        <Input
          className="h-9 w-30.5 shrink-0"
          containerSize="sm"
          leadingComponent={<span>px</span>}
          value={fontSize}
          onValidateInternalValue={(e) => {
            if (e.target.value === "") return true;
            const isNumber = /^\d+$/.test(e.target.value);
            if (!isNumber) return false;
            const value = parseInt(e.target.value);
            return value >= 0 && value <= 100001;
          }}
          onValidate={(e) => {
            return /^\d+$/.test(e.target.value);
          }}
          onChange={(e) => {
            onFontSizeChange(parseInt(e.target.value));
          }}
        />
      </div>
      <div className="flex gap-4">
        <Select
          value={fontWeight.toString()}
          onValueChange={(newFontWeight) =>
            onFontWeightChange(parseInt(newFontWeight))
          }
        >
          <SelectTrigger className="h-9 w-50 flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {weightOptions.map((weight) => (
              <SelectItem key={weight} value={weight.toString()}>
                {fontWeightLabels[weight] ?? weight}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={fontStyle}
          onValueChange={(newFontStyle) =>
            onFontStyleChange(newFontStyle === "italic" ? "italic" : "normal")
          }
        >
          <SelectTrigger className="h-9 w-50 flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {styleOptions.map((style) => (
              <SelectItem key={style} value={style}>
                {styleLabels[style] ?? style}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
